import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@wa/ui";
import { FOCUSABLE, focusableStops, moveWithinGroup, stepFocus } from "./popFocus";

/**
 * The app's dropdowns.
 *
 * They share one open slot rather than each tracking their own boolean: three
 * menus hanging off the same 74px strip would otherwise be able to overlap, and
 * a click on the second one has to close the first in the same tick or the page
 * flickers through a state where both are up.
 */
interface PopBus {
  open: string | null;
  toggle: (id: string) => void;
  close: () => void;
}

const PopContext = createContext<PopBus | null>(null);

export function PopHost({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<string | null>(null);

  const bus = useMemo<PopBus>(
    () => ({
      open,
      toggle: (id) => setOpen((cur) => (cur === id ? null : id)),
      close: () => setOpen(null),
    }),
    [open],
  );

  useEffect(() => {
    if (!open) return;
    const onDown = () => setOpen(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Already answered by a layer above - see the same check in `Pop`'s own
      // handler. Both are needed: a guard in only one of them leaves the other
      // to close the menu, which measures the same as having no guard at all.
      if (e.defaultPrevented) return;
      // Stopped here, on the document, so one press closes ONE layer. Every
      // other Escape owner in this app listens on `window` - both modal shells,
      // the command palette, the call window, the media picker - and window is
      // the next hop after document, so without this a menu opened inside a
      // dialog took the dialog with it. The modals' own guard does not help:
      // it only steps aside for a Radix popper, and this menu is not one.
      e.preventDefault();
      e.stopPropagation();
      setOpen(null);
    };
    // The panel's own handler consumes Escape while focus is inside it (see
    // `Pop`); this is the fallback for a menu opened with the mouse whose panel
    // had nothing focusable to take.
    document.addEventListener("mousedown", onDown);
    // And touchstart, because iOS only synthesises a mouse event for a tap on
    // something it already considers clickable. A tap on the padding of a card
    // beside an open filter menu produced no `mousedown` at all, and the menu
    // stayed up until something else was pressed.
    //
    // This one only works because `Pop` stops touchstart on its wrapper, which
    // the panel's rows bubble through as well - a portal leaves the DOM tree
    // but not the React one. touchstart is the first event of a tap, so without
    // that stop this listener tore the panel out from under the finger before
    // the browser got as far as sending the `click`: the menu shut and the row
    // it was shut on never ran. Anything added here has to be stopped there too.
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return <PopContext.Provider value={bus}>{children}</PopContext.Provider>;
}

export function useClosePops(): () => void {
  return useContext(PopContext)?.close ?? (() => {});
}

/**
 * The id of whichever dropdown is up, or null.
 *
 * Only the right-click menu reads this, and only to get out of the way: a `Pop`
 * stops the mousedown that opens it, so the context menu's own outside-click
 * listener never hears about it and the two would otherwise be up at once.
 */
export function usePopOpen(): string | null {
  return useContext(PopContext)?.open ?? null;
}

/** Gap between the trigger and the panel, and the least the panel keeps from
 *  the window edge. */
const GAP = 12;
const EDGE = 10;

interface Placement {
  top: number;
  left: number;
  /** Flipped above the trigger, which the entry animation has to match. */
  up: boolean;
  /** Set only under `matchWidth`: the trigger's width, handed to the panel. */
  width?: number;
}

/**
 * One dropdown: `trigger` gets the open state so the button can style itself,
 * `children` is the menu body. The panel is only mounted while open, which is
 * what lets it play its entry animation every time.
 *
 * The panel is rendered into `document.body` and positioned against the
 * trigger's box, rather than absolutely inside its own wrapper. That is not
 * tidiness: every pane in this app is a `crm-work-col`, which clips its
 * overflow to keep the card's 28px corners, and a list row does the same. An
 * absolutely positioned panel opened from a pane footer or from the last row of
 * a list is simply cut off, with nothing in the console to say so. Positioning
 * against the viewport also lets the panel flip above the trigger when there is
 * no room below, and stay inside the window horizontally - both of which a
 * `right: 0` rule cannot do.
 */
export function Pop({
  trigger,
  children,
  wide,
  matchWidth,
  className,
}: {
  trigger: (open: boolean) => ReactNode;
  children: ReactNode;
  wide?: boolean;
  /**
   * Hang the panel under the trigger's left edge and give it the trigger's
   * width, instead of the default right-aligned content-width panel.
   *
   * For a toolbar filter the default is right: the trigger is small, the panel
   * is wider than it, and growing leftwards keeps it inside the pane. A form
   * field is the other way round - it is as wide as its column, and a 232px
   * menu pinned to its right edge reads as belonging to whatever is next to it
   * rather than to the field it opened from.
   */
  matchWidth?: boolean;
  className?: string;
}) {
  const bus = useContext(PopContext);
  const id = useId();
  const open = bus?.open === id;

  /**
   * A `Pop` with no `PopHost` above it is inert, and inert in the worst way: it
   * renders, it accepts the click, and nothing happens. `bus` is null, so `open`
   * can never be true and `toggle` is a no-op - there is no error anywhere and
   * the panel simply does not exist.
   *
   * It has happened twice, and the second time (the register map's filter rail,
   * 2026-08-16) it shipped, because the map needs a Google Maps key to draw at
   * all and nobody had ever clicked those menus on a rendered map. Each page
   * mounts its own host, so a new page is exactly where this gets forgotten.
   *
   * Dev only. In production a missing host is still a dead menu, but a console
   * error in front of an operator helps nobody.
   */
  if (import.meta.env.DEV && !bus) {
    console.error(
      "Pop: no PopHost above this dropdown, so it will never open. Wrap the page in <PopHost>.",
    );
  }
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Placement | null>(null);
  /** One entry into the panel per opening, however often `place` is recomputed
   *  (every scroll and resize recomputes it). */
  const entered = useRef(false);

  /**
   * Kept away from the document, where `PopHost` closes whatever is open.
   *
   * Both pointer kinds, because the host listens for both. A mouse press was
   * always stopped here; a tap was not, and a tap leads with `touchstart` -
   * so on a phone every menu closed on the way down and the `click` that
   * should have followed landed on a row that had already left the document.
   * Typed on the shared base so one callback covers both handlers.
   */
  const onDown = useCallback((e: React.SyntheticEvent) => e.stopPropagation(), []);

  /** The real control the call site handed us. `Pop` only wraps it in a span,
   *  so this is the only handle it has on the thing focus came from. */
  const triggerEl = useCallback(() => anchorRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? null, []);

  const closeAndReturn = useCallback(() => {
    // Focus first, close second: the panel is about to unmount and focus would
    // fall to <body>, which loses the operator's place in the page entirely.
    triggerEl()?.focus();
    bus?.close();
  }, [bus, triggerEl]);

  const step = useCallback((delta: number, from?: number) => stepFocus(panelRef.current, delta, from), []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        // Somebody above us already answered this press. A Radix popper opened
        // from a row in this panel - the deadline window's two edges are the
        // shared calendar - portals to the body but stays a React DESCENDANT of
        // the panel, so its keydown arrives here too, and closing the menu
        // would take the calendar down with it in one press.
        //
        // `defaultPrevented` rather than looking for the popper in the DOM.
        // Radix dismisses from a CAPTURE-phase listener and calls
        // `preventDefault` when it does, both of which happen before React
        // builds this synthetic event - so the flag is already set and there is
        // no race to lose. Every DOM test we tried had one: the popper is gone
        // by the time a bubble listener looks for it, and its `data-state` has
        // already flipped to "closed" by the time this handler runs.
        if (e.defaultPrevented) return;
        // Consumed here rather than left to the host's document listener, so it
        // never reaches a dialog behind the menu. React attaches this portal's
        // listeners to document.body, one hop below document.
        e.preventDefault();
        e.stopPropagation();
        closeAndReturn();
        return;
      case "ArrowDown":
        e.preventDefault();
        step(1);
        return;
      case "ArrowUp":
        e.preventDefault();
        step(-1);
        return;
      case "Home":
        e.preventDefault();
        step(1, -1);
        return;
      case "End":
        e.preventDefault();
        step(-1, -1);
        return;
      case "ArrowRight":
        if (moveWithinGroup(document.activeElement, 1)) e.preventDefault();
        return;
      case "ArrowLeft":
        if (moveWithinGroup(document.activeElement, -1)) e.preventDefault();
        return;
      case "Tab":
        // Not prevented: closing and handing focus back to the trigger is
        // enough, and the browser then tabs onwards from there. A focus trap
        // would be wrong here - these are dropdowns, not dialogs, and one row
        // menu sits under a link overlay it must not fight with.
        closeAndReturn();
        return;
      default:
    }
  };

  /**
   * Move focus into the panel once it has a position.
   *
   * Keyed on `place` on purpose: for the first committed frame the panel is
   * `visibility: hidden` while it is being measured, and nothing inside a
   * hidden subtree can take focus - `.focus()` there is a silent no-op.
   *
   * When there is nothing to land on, focus parks on the panel itself so that
   * Escape and the arrow keys still work, and a MutationObserver waits for rows
   * that arrive over the network (templates, unread mail). It only ever moves
   * focus that is still on the panel, so a menu whose list resolves late cannot
   * yank the caret out of wherever the operator has since typed.
   */
  useLayoutEffect(() => {
    if (!open || !place) return;
    const panel = panelRef.current;
    if (!panel || entered.current) return;
    entered.current = true;

    const first = focusableStops(panel)[0];
    if (first) {
      first.focus();
      return;
    }
    panel.focus({ preventScroll: true });
    const observer = new MutationObserver(() => {
      if (document.activeElement !== panel) return observer.disconnect();
      const row = focusableStops(panel)[0];
      if (!row) return;
      row.focus();
      observer.disconnect();
    });
    observer.observe(panel, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, place]);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    entered.current = false;
    if (!wasOpen.current) return;
    wasOpen.current = false;
    // The panel has just unmounted and took focus with it. Every close path
    // ends up here - Escape, an outside click, and the nine call sites that
    // call `useClosePops()` after acting - and only the ones that moved focus
    // somewhere themselves leave anything but <body> behind, which is why the
    // check is on <body> rather than on which path we came from.
    if (document.activeElement === null || document.activeElement === document.body) triggerEl()?.focus();
  }, [open, triggerEl]);

  // Layout, not effect: the measurement has to land before the browser paints,
  // or the panel shows up in the top-left corner for one frame on the way to
  // where it belongs.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const position = () => {
      const el = panelRef.current;
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor || !el) return;
      // The panel is measured with `offsetWidth`/`offsetHeight` and NOT with a
      // rect, because a rect carries transforms and this panel is always
      // mid-animation when it is first measured: `crm-pop` starts at
      // `scale(0.98)` and fills backwards, so the rect is 2% short before the
      // animation has run a single frame. Two per cent of a 330px menu is 7px,
      // and because the panel hangs off the trigger's RIGHT edge the whole 7px
      // lands on `left` - the menu opened a nudge too far right and slid back
      // the moment anything recomputed it. The anchor keeps its rect: it is not
      // animating, and only its viewport position is wanted.
      const panelW = el.offsetWidth;
      const panelH = el.offsetHeight;
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      // Above only when there is genuinely no room below AND there is room
      // above. A menu that flips at the first opportunity jumps over the
      // pointer for no reason the operator can see.
      const up = anchor.bottom + GAP + panelH > vh - EDGE && anchor.top - GAP - panelH > EDGE;
      const top = up ? anchor.top - GAP - panelH : Math.min(anchor.bottom + GAP, vh - EDGE - panelH);
      // Under `matchWidth` the panel is as wide as the trigger and grows to the
      // right from its left edge; otherwise it is content-width and grows to
      // the left from the trigger's right edge. Either way it is then pulled
      // back inside the window - measured on the panel, because the stylesheet
      // keeps a floor under the width and a narrow field does not get a menu
      // narrower than its own rows.
      const width = Math.max(panelW, matchWidth ? anchor.width : 0);
      const wanted = matchWidth ? anchor.left : anchor.right - width;
      const left = Math.min(Math.max(EDGE, wanted), Math.max(EDGE, vw - EDGE - width));
      setPlace({ top: Math.max(EDGE, top), left, up, width: matchWidth ? anchor.width : undefined });
    };

    position();
    // A menu whose contents arrive over the network (templates, clients) grows
    // after it opens; without this it would keep the height it had while empty.
    const observer = new ResizeObserver(position);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener("resize", position);
    // Capture, so a scroll inside any pane moves the panel with its trigger.
    window.addEventListener("scroll", position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, matchWidth]);

  /**
   * Where the panel is portaled to.
   *
   * ⚠ `document.body` is wrong whenever something on the page has been made
   * NATIVE fullscreen. The browser renders only the fullscreen element's
   * subtree, so a panel portaled to the body is not merely behind it - it is not
   * drawn at all, and the menu reads as a button that stopped working. The map's
   * fullscreen mode hit exactly this: z-index was never involved (the panel is
   * at 97, the map layer at 70), the panel was simply outside what the browser
   * was showing.
   *
   * Read at render time rather than held in state. A menu is opened by a click,
   * which re-renders this component, so the target is always resolved against
   * the fullscreen state in force when the panel actually appears.
   */
  const portalTo =
    (typeof document !== "undefined" && document.fullscreenElement) || document.body;

  return (
    <div
      className={cn("crm-pop", open && "is-open", className)}
      onMouseDown={onDown}
      // Without this a tap anywhere in the menu was eaten before it did
      // anything: the host closed on `touchstart` and the `click` half a
      // gesture later landed on a row that had already left the document, so
      // the menu shut and nothing happened. Covers the panel as well as the
      // trigger - the portal is outside the DOM tree but still inside this
      // React one, so its rows bubble through here.
      onTouchStart={onDown}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        ref={anchorRef}
        onClick={() => bus?.toggle(id)}
        // ArrowDown opens, the way a menu button is expected to. Enter and Space
        // already worked, but only by accident: they fire a click on the call
        // site's button and it bubbles to this span.
        onKeyDown={(e) => {
          if (open || e.key !== "ArrowDown") return;
          e.preventDefault();
          bus?.toggle(id);
        }}
      >
        {trigger(open)}
      </span>
      {open
        ? createPortal(
            // The wrapper's React handlers still catch these clicks - a portal
            // keeps the React tree even when it leaves the DOM tree - so the
            // host's document listener never sees a click inside the menu.
            <div
              ref={panelRef}
              // Focusable but not tabbable: somewhere for focus to sit while the
              // rows are still loading, so Escape and the arrows work on a panel
              // that has nothing in it yet.
              tabIndex={-1}
              className={cn("crm-menu", wide && "crm-menu-wide", place?.up && "crm-menu-up")}
              style={{
                top: place?.top ?? 0,
                left: place?.left ?? 0,
                // Handed to CSS as a variable rather than written straight onto
                // `min-width`, so the stylesheet's own floor still wins for a
                // field narrower than one menu row.
                ...(place?.width ? ({ "--crm-pop-w": `${place.width}px` } as CSSProperties) : null),
                visibility: place ? "visible" : "hidden",
              }}
              onKeyDown={onKeyDown}
              onMouseDown={onDown}
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </div>,
            portalTo,
          )
        : null}
    </div>
  );
}
